
import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Popover,
  Backdrop,
  CircularProgress
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { SketchPicker } from "react-color";
import ChartPreview from "./ChartPreview";
import FetchData from "./ServerCall/FetchData";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { ColorSchemes } from "./ChartOptions/Color/ColorScheme";
import { clearCachedThemes } from "../api/fetchUserThemes";

export interface ThemeObject {
  themeId: string;
  themeName: string;
  background: string;
  tableHeaderBackground: string;
  colors: string[];
  isDefault?: boolean;
}
interface ThemeEditorProps {
  savedThemes: string[];
  setSavedThemes: React.Dispatch<React.SetStateAction<string[]>>;
  onBack: () => void;
  addUserTheme: (theme: ThemeObject) => void;
  updateUserTheme: (theme: ThemeObject) => void;
  editingTheme?: ThemeObject | null;
  refreshThemes: () => Promise<void>;
}

const defaultPeacockTheme = {
  themeId: '',
  themeName: 'peacock',
  background: '#ffffff',
  tableHeaderBackground: '#f7f7f7',
  colors: [
    '#2bb9bb',
    '#af99db',
    '#5ab1ef',
    '#ffb980',
    '#d87a80',
    '#8d98b3',
    '#e5cf0d',
    '#97b552',
    '#95706d',
    '#dc69aa',
  ],
};

const isColorDark = (hexColor: string) => {
  const r = parseInt(hexColor.substring(1, 3), 16);
  const g = parseInt(hexColor.substring(3, 5), 16);
  const b = parseInt(hexColor.substring(5, 7), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128;
};

const ThemeEditor: React.FC<ThemeEditorProps> = ({
  savedThemes,
  setSavedThemes,
  onBack,
  addUserTheme,
  updateUserTheme,
  editingTheme = null,
  refreshThemes,
}) => {
  const [theme, setTheme] = useState({ ...defaultPeacockTheme });
  const [showWarning, setShowWarning] = useState(false);
  const [showLengthWarning, setShowLengthWarning] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentColorField, setCurrentColorField] = useState<{
    type: 'background' | 'tableHeaderBackground' | 'colors';
    index?: number;
  } | null>(null);

  const [showApiError, setShowApiError] = useState(false);
  const [showEmptyNameWarning, setShowEmptyNameWarning] = useState(false);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (editingTheme) {
      setTheme({
        themeId: editingTheme.themeId,
        themeName: editingTheme.themeName,
        background: editingTheme.background,
        tableHeaderBackground: editingTheme.tableHeaderBackground,
        colors: editingTheme.colors,
      });
    } else {
      setTheme({ ...defaultPeacockTheme });
    }
  }, [editingTheme]);

  const handleOpenPicker = (
    event: React.MouseEvent<HTMLElement>,
    type: 'background' | 'tableHeaderBackground' | 'colors',
    index?: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentColorField({ type, index });
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
    setCurrentColorField(null);
  };

  const handleColorChange = (color: any) => {
    const hex = color.hex;
    if (!currentColorField) return;

    if (currentColorField.type === 'colors' && typeof currentColorField.index === 'number') {
      const updatedColors = [...theme.colors];
      updatedColors[currentColorField.index] = hex;
      setTheme({ ...theme, colors: updatedColors });
    } else {
      setTheme({ ...theme, [currentColorField.type]: hex });
    }
  };

  const handleAddColor = () => {
    if (theme.colors.length >= 20) return;

    const randomPeacockColor =
      defaultPeacockTheme.colors[Math.floor(Math.random() * defaultPeacockTheme.colors.length)];

    setTheme({ ...theme, colors: [...theme.colors, randomPeacockColor] });
  };

  const handleRemoveColor = (index: number) => {
    if (theme.colors.length <= 5) return;
    const newColors = [...theme.colors];
    newColors.splice(index, 1);
    setTheme({ ...theme, colors: newColors });
  };

  const handleSave = async () => {
    const themeName = theme.themeName.trim();

     if (themeName === '') {
       setShowEmptyNameWarning(true);
       return;
     }

    if (themeName.length > 30) {
      setShowLengthWarning(true);
      return;
    }

    const lowerThemeName = themeName.toLowerCase();
    const silzilaThemeNames = ColorSchemes.map((scheme) => scheme.name.toLowerCase());

    const isDuplicate =
      savedThemes.some(
        (name) =>
          name === lowerThemeName &&
          (!editingTheme || lowerThemeName !== editingTheme.themeName.toLowerCase()),
      ) || silzilaThemeNames.includes(lowerThemeName);

    if (isDuplicate) {
      setShowWarning(true);
      return;
    }

    const fontColor = isColorDark(theme.background) ? '#ffffff' : '#3b3b3b';

    const themePayload = {
      themeId: theme.themeId,
      themeName,
      isDefault: editingTheme?.isDefault ?? false,
      theme: JSON.stringify({
        background: theme.background,
        tableHeaderBackground: theme.tableHeaderBackground,
        colors: theme.colors,
        fontColor: fontColor,
      }),
    };

    try {
      setLoading(true);

      const method = editingTheme ? 'PUT' : 'POST';
      const url = editingTheme ? `theme/update` : 'theme';

      const response = await FetchData({
        requestType: 'withData',
        method,
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        data: themePayload,
      });

      if (response.status) {
        const themeId = (editingTheme as ThemeObject | null)?.themeId ?? response.data.themeId;

        const newTheme = {
          themeId,
          themeName,
          background: theme.background,
          tableHeaderBackground: theme.tableHeaderBackground,
          colors: theme.colors,
          fontColor: fontColor,
        };

        if (editingTheme) {
          updateUserTheme({ ...newTheme, isDefault: editingTheme.isDefault });
        } else {
          setSavedThemes([...savedThemes, themeName.toLowerCase()]);
          addUserTheme(newTheme);
        }

        clearCachedThemes();
        await refreshThemes();
        onBack();
      } else {
        throw new Error(response.error || 'Failed to save theme');
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      setShowApiError(true);
    } finally {
      setLoading(false); 
    }
  };

  const LabelBox = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ width: 150, textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#333' }}>
      {children}
    </Box>
  );

  return (
    <Box style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
      <Button
        onClick={onBack}
        variant="outlined"
        startIcon={<KeyboardArrowLeftIcon sx={{ marginRight: '-6px' }} />}
        sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, padding: '3px 5px' }}
      >
        Back
      </Button>

      <Box display="flex" gap={4}>
        <Box
          sx={{
            height: '85vh',
            width: 'fit-content',
            borderRadius: 2,
            padding: 0,
            marginRight: '60px',
            marginLeft: '60px',
            display: 'flex',
            flexDirection: 'column',
            transform: 'TranslateY(-20px)',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              padding: 2,
              pr: 3,
            }}
          >
            <Box display="flex" flexDirection="column" gap={2} mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <LabelBox>Table Background</LabelBox>
                <TextField
                  value={theme.background}
                  onChange={(e) => setTheme({ ...theme, background: e.target.value })}
                  sx={{ width: 160 }}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(e) => handleOpenPicker(e, 'background')}
                          edge="end"
                          style={{
                            backgroundColor: theme.background,
                            border: '1px solid #ccc',
                            width: 19,
                            height: 19,
                            borderRadius: 0,
                            marginRight: -7,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <LabelBox>Table Header Background</LabelBox>
                <TextField
                  value={theme.tableHeaderBackground}
                  onChange={(e) => setTheme({ ...theme, tableHeaderBackground: e.target.value })}
                  sx={{ width: 160 }}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(e) => handleOpenPicker(e, 'tableHeaderBackground')}
                          edge="end"
                          style={{
                            backgroundColor: theme.tableHeaderBackground,
                            border: '1px solid #ccc',
                            width: 19,
                            height: 19,
                            borderRadius: 0,
                            marginRight: -7,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={2} mb={2} marginTop={-1}>
              <LabelBox>Chart Colors</LabelBox>
              <Box display="flex" flexDirection="column" gap={1}>
                {theme.colors.map((color, index) => (
                  <Box key={index}>
                    <TextField
                      value={color}
                      onChange={(e) => {
                        const updated = [...theme.colors];
                        updated[index] = e.target.value;
                        setTheme({ ...theme, colors: updated });
                      }}
                      sx={{ width: 160 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={(e) => handleOpenPicker(e, 'colors', index)}
                              edge="end"
                              style={{
                                backgroundColor: color,
                                border: '1px solid #ccc',
                                width: 19,
                                height: 19,
                                borderRadius: 0,
                                marginRight: -7,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {index === theme.colors.length - 1 && (
                      <Box display="flex" gap={2} mt={0.5} marginTop={2}>
                        {theme.colors.length > 5 && index >= 5 ? (
                          <Button
                            onClick={() => handleRemoveColor(index)}
                            size="small"
                            variant="outlined"
                            style={{ width: '70px' }}
                            startIcon={
                              <RemoveIcon
                                sx={{ marginRight: '-5px', height: '10px', width: '10px' }}
                              />
                            }
                            sx={{
                              minWidth: 'auto',
                              padding: '1px 4px',
                              color: '#9e9c9cff',
                              borderColor: '#9e9c9cff',
                              marginLeft: '3px',
                              '&:hover': {
                                color: 'error.main',
                                borderColor: 'error.main',
                                backgroundColor: 'rgba(211, 47, 47, 0.05)',
                              },
                            }}
                          >
                            DELETE
                          </Button>
                        ) : (
                          <Button
                            disabled
                            size="small"
                            variant="outlined"
                            style={{ width: '70px', visibility: 'hidden' }} // keeps width but hides visually
                          >
                            DELETE
                          </Button>
                        )}

                        <Button
                          onClick={handleAddColor}
                          size="small"
                          variant="outlined"
                          style={{
                            width: '70px',
                            visibility: theme.colors.length < 20 ? 'visible' : 'hidden',
                          }}
                          startIcon={
                            <AddIcon sx={{ marginRight: '-5px', height: '10px', width: '10px' }} />
                          }
                          sx={{
                            minWidth: 'auto',
                            padding: '1px 4px',
                            color: '#9e9c9cff',
                            borderColor: '#9e9c9cff',
                            '&:hover': {
                              color: '#2bb9bb',
                              borderColor: '#2bb9bb',
                            },
                          }}
                        >
                          ADD
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              padding: '15px 5px 20px 20px',
              backgroundColor: '#fff',
              zIndex: 1,
              display: 'flex',
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <LabelBox>Theme Name</LabelBox>
              <TextField
                value={theme.themeName}
                onChange={(e) => setTheme({ ...theme, themeName: e.target.value })}
                sx={{ width: 150, transform: 'TRanslateX(-80px)' }}
                size="small"
              />
            </Box>
            <Button
              onClick={handleSave}
              sx={{
                color: '#2bb9bb',
                border: '1px solid #2bb9bb',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(43, 185, 187, 0.05)',
                  border: '1px solid #2bb9bb',
                },
                marginRight: '25px',
                height: '25px',
                marginTop: '5px',
              }}
            >
              {editingTheme ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 500, paddingTop: 0 }}>
          <Box mb={2}>
            <ChartPreview
              colors={theme.colors}
              background={theme.background}
              tableHeaderBackground={theme.tableHeaderBackground}
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={showApiError} onClose={() => setShowApiError(false)}>
        <DialogContent>Failed to save theme.</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowApiError(false)}
            style={{ border: '1px solid #2bb9bb', padding: '1px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <SketchPicker
          color={
            currentColorField?.type === 'colors'
              ? theme.colors[currentColorField.index!]
              : theme[currentColorField?.type || 'background']
          }
          onChange={handleColorChange}
          disableAlpha
        />
      </Popover>

      <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
        <DialogContent>Theme name already exists</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowWarning(false)}
            style={{ border: '1px solid #2bb9bb', padding: '1px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEmptyNameWarning} onClose={() => setShowEmptyNameWarning(false)}>
        <DialogContent>Theme name cannot be empty</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowEmptyNameWarning(false)}
            style={{ border: '1px solid #2bb9bb', padding: '1px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLengthWarning} onClose={() => setShowLengthWarning(false)}>
        <DialogContent>Theme name exceeds 30 characters</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowLengthWarning(false)}
            style={{ border: '1px solid #2bb9bb', padding: '1px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};


export default ThemeEditor;
